import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { PageAccessErrorComponent, PageError } from "@/features/auth/components/PageError";
import { ManageDeliveryPage } from "@/features/script-delivery/pages/ManageDeliveryPage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import { PageAccessError, orderStatusTimeDashboardQueryOptions, pageStateQueryOptions } from "@/lib/queries";
import { scriptDeliverySummaryQueryOptions } from "@/lib/queries/script-delivery.queries";

export const Route = createFileRoute("/_authenticated/script-delivery")({
  beforeLoad: () => {
    assertPagePermission("script-delivery");
    const { tenants, activeTenantId } = useAuth.getState();
    const activeTenant = tenants.find((tenant) => tenant.id === activeTenantId);
    if (activeTenant && activeTenant.type !== "SUPER_TENANT") {
      throw new PageAccessError("script-delivery");
    }
  },
  loader: async ({ context }) => {
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("script-delivery"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
    void context.queryClient.prefetchQuery(scriptDeliverySummaryQueryOptions());
    void context.queryClient.prefetchQuery(orderStatusTimeDashboardQueryOptions());
    return pageState;
  },
  component: ScriptDeliveryRoute,
  errorComponent: PageAccessErrorComponent,
});

function ScriptDeliveryRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.scriptDelivery"));
  const { isLoading, error } = usePageState("script-delivery");

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">{t("scriptDelivery.loading")}</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <ManageDeliveryPage includeScriptDelivery />;
}
